import { useCallback, useState } from 'react'
import axios from 'axios'
import config from 'config'
import { useTranslation } from 'react-i18next'

import {
  SESSIONS_STEP_CONFIRMATION,
  SESSIONS_STEP_PLANNING,
  SESSIONS_STEP_PRICING,
} from 'components/organisms/Sessions/sessionsLib'

import useSnackbar from '../components/organisms/Snackbar/useSnackbar'

const useSession = () => {
  const { t } = useTranslation()
  const { showSnackbarMessage } = useSnackbar()

  const [state, setState] = useState({
    isFetching: false,
    isUpdating: false,
    session: {},
    error: null,
  })

  const parseError = useCallback(
    (e, defaultMessage) => {
      // let message = defaultMessage || e.message
      let message = defaultMessage || e.response?.data?.message

      if (Number(e?.response?.status) === 400) {
        if (e.response?.data?.message?.startsWith('Duplicated plates:')) {
          message = t('common.error.sessions.fileUpload.duplicatedPlates', {
            attribute: e.response.data.message.split(':')[1],
          })
        }
        if (
          e.response?.data?.message?.startsWith(
            'Session is not in planning status'
          )
        ) {
          message = t('common.error.sessions.notInPlanning')
        }
        if (
          e.response?.data?.message?.endsWith('the session is not confirmed')
        ) {
          message = t('common.error.sessions.notConfirmed')
        }
      }

      showSnackbarMessage({
        severity: 'error',
        message,
      })
    },
    [showSnackbarMessage, t]
  )

  const setStartFetching = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: true,
      isUpdating: false,
    }))
  }, [setState])

  const setStopFetching = useCallback(
    (error) => {
      setState((s) => ({
        ...s,
        isFetching: false,
        error: error,
      }))
    },
    [setState]
  )

  const setStartUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isFetching: false,
      isUpdating: true,
    }))
  }, [setState])

  const setStopUpdating = useCallback(() => {
    setState((s) => ({
      ...s,
      isUpdating: false,
    }))
  }, [setState])

  const setSuccessFetching = useCallback(
    (session) => {
      setState((s) => ({
        ...s,
        session,
        isFetching: false,
        isUpdating: false,
        error: null,
      }))
    },
    [setState]
  )

  const fetchSession = useCallback(
    async (sessionId) => {
      setStartFetching()

      try {
        const results = await axios.get(
          `${config.apiBaseUrl}sessions/${sessionId}`
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        let message = t('common.error.fetchData', {
          attribute: e.response?.status,
        })

        setStopFetching(e)

        showSnackbarMessage({
          severity: 'error',
          message,
        })
      }
    },
    [
      setStartFetching,
      setStopFetching,
      setSuccessFetching,
      showSnackbarMessage,
      t,
    ]
  )

  const fetchCurrentSession = useCallback(async () => {
    await fetchSession('current')
  }, [fetchSession])

  const fetchCurrentAssignmentSession = useCallback(async () => {
    await fetchSession('current-assignment')
  }, [fetchSession])

  const fetchCurrentCarsBookedSession = useCallback(async () => {
    await fetchSession('current-cars-booked')
  }, [fetchSession])

  const updateDate = useCallback(
    async (sessionId, sessionAgendaId, type, date) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/agenda/${sessionAgendaId}`,
          { type, date }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const updateAllDates = useCallback(
    async (sessionId, data) => {
      setStartUpdating()

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/agenda`,
          { ...data }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const fetchCarsData = useCallback(
    async (sessionId) => {
      setStartUpdating()

      try {
        await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/ingest-cars`,
          {}
        )

        await fetchSession(sessionId)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [fetchSession, parseError, setStartUpdating, setStopUpdating]
  )

  const updatePrices = useCallback(
    async (sessionId, cars, fileName) => {
      setStartUpdating()

      try {
        const results = await axios.patch(
          `${config.apiBaseUrl}sessions/${sessionId}/cars-pricing`,
          { cars, fileName }
        )

        setSuccessFetching(results.data.body)
      } catch (e) {
        setStopUpdating()

        parseError(e)
      }
    },
    [parseError, setStartUpdating, setStopUpdating, setSuccessFetching]
  )

  const confirmStep = useCallback(
    async (sessionId, step, callback) => {
      setStartUpdating()
      const postStep =
        step === SESSIONS_STEP_PLANNING
          ? 'date'
          : step === SESSIONS_STEP_PRICING
          ? 'cars-pricing'
          : step === SESSIONS_STEP_CONFIRMATION
          ? 'session'
          : step

      try {
        const results = await axios.post(
          `${config.apiBaseUrl}sessions/${sessionId}/${postStep}/confirm`,
          {}
        )

        setSuccessFetching({ ...state.session, ...results.data.body })
        callback && callback()
        // setStopUpdating()
      } catch (e) {
        setStopUpdating()

        showSnackbarMessage({
          severity: 'error',
          message: e.response?.data?.message,
        })
      }
    },
    [
      setStartUpdating,
      setStopUpdating,
      setSuccessFetching,
      showSnackbarMessage,
      state.session,
    ]
  )

  return {
    session: state.session,
    isFetching: state.isFetching,
    isUpdating: state.isUpdating,
    error: state.error,
    fetchSession,
    fetchCurrentSession,
    fetchCurrentAssignmentSession,
    fetchCurrentCarsBookedSession,
    updateDate,
    updateAllDates,
    updatePrices,
    fetchCarsData,
    confirmStep,
  }
}

export default useSession
